/**
 * #625 — the ONE control for what a discount covers (plan md/plan/625.md §5.1). Used by the
 * catalogue form, the school default (Discount settings), and the one-off fields on the invoice
 * page, the draft edit and the amend.
 *
 * - Tuition / Other charges ticks. Ticking Tuition ticks Other too (D1): lines typed by hand are
 *   "Other charges", and most of MNM's are tuition. Other off beside Tuition shows the caption, and
 *   so does Tuition without "All products" (tuition sold as a product is a product, /qa #625 M1).
 * - Products: All / None / Chosen categories (+ an "Uncategorised products" tick). "All" is its
 *   own flag, so a category made later is covered by an "all products" discount (D2).
 * - Nothing chosen is shown as an error here; the SERVER refuses it (DISCOUNT_NO_COVERAGE).
 *
 * The value is always sent whole (the five keys), never half: the server has no defaults.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import type { DiscountCoverage } from '../../../types/discount.types';
import { useProductCategories } from '../../../hooks/useProducts';
import { coverageIsEmpty, describeCoverage, sameCoverage } from '../../../utils/discountCoverage';

type ProductMode = 'all' | 'none' | 'chosen';

const modeOf = (c: DiscountCoverage): ProductMode =>
  c.coversAllProducts ? 'all' : c.coversCategoryIds.length > 0 || c.coversUncategorised ? 'chosen' : 'none';

export interface DiscountCoverageFieldsProps {
  value: DiscountCoverage;
  onChange: (next: DiscountCoverage) => void;
  /** The school default: shows "Use the school default" when the value differs from it. */
  defaultCoverage?: DiscountCoverage;
  /**
   * The category list needs operations_pricing:read (#272). Default on. Without it "Chosen
   * categories" cannot be offered (there is nothing to choose from): it is shown disabled.
   */
  canReadCategories?: boolean;
  disabled?: boolean;
  /** A heading other than "What it covers". */
  label?: string;
}

export default function DiscountCoverageFields({
  value,
  onChange,
  defaultCoverage,
  canReadCategories = true,
  disabled = false,
  label = 'What it covers',
}: DiscountCoverageFieldsProps) {
  const { data: categories = [], isSuccess: namesReady } = useProductCategories({ enabled: canReadCategories });
  const names = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);

  // "Chosen" with nothing picked yet reads as "none" from the value alone, so the radio keeps
  // its own state and follows the value only when the value says otherwise.
  const [mode, setMode] = useState<ProductMode>(modeOf(value));
  useEffect(() => {
    const derived = modeOf(value);
    setMode((current) => (derived === 'none' && current === 'chosen' ? current : derived));
  }, [value]);

  const set = (patch: Partial<DiscountCoverage>) => onChange({ ...value, ...patch });

  const onMode = (next: ProductMode) => {
    setMode(next);
    if (next === 'all') set({ coversAllProducts: true, coversUncategorised: false, coversCategoryIds: [] });
    else set({ coversAllProducts: false, coversUncategorised: false, coversCategoryIds: [] });
  };

  // A chosen id the school no longer has (deleted since) stays visible, so saving keeps it knowingly.
  const options = useMemo(() => {
    const known = categories.map((c) => ({ id: c.id, name: c.name }));
    const missing = value.coversCategoryIds
      .filter((id) => !names.has(id))
      // Until the list has loaded a chosen id is not yet known to be gone.
      .map((id) => ({ id, name: namesReady ? '(deleted category)' : 'Loading…' }));
    return [...known, ...missing];
  }, [categories, names, namesReady, value.coversCategoryIds]);
  const selected = options.filter((o) => value.coversCategoryIds.includes(o.id));

  const productsLabelId = useId();
  const empty = coverageIsEmpty(value);
  const otherOffBesideTuition = value.coversTuition && !value.coversOther;
  const tuitionProductsLeftOut = value.coversTuition && !value.coversAllProducts;
  const showReset = defaultCoverage && !sameCoverage(value, defaultCoverage);

  return (
    <FormControl component="fieldset" disabled={disabled} error={empty} data-testid="discount-coverage-fields">
      <FormLabel component="legend">{label}</FormLabel>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={value.coversTuition}
              // D1: ticking Tuition ticks Other charges too (typed lines are mostly tuition).
              onChange={(e) => set(e.target.checked ? { coversTuition: true, coversOther: true } : { coversTuition: false })}
              inputProps={{ 'aria-label': 'Tuition' }}
            />
          }
          label="Tuition"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.coversOther}
              onChange={(e) => set({ coversOther: e.target.checked })}
              inputProps={{ 'aria-label': 'Other charges' }}
            />
          }
          label="Other charges"
        />
      </FormGroup>
      {otherOffBesideTuition && (
        <FormHelperText error={false} sx={{ ml: 0 }} data-testid="coverage-other-caption">
          Lines typed by hand count as Other charges, and most of them are tuition. Keep it ticked unless you mean
          to leave them out.
        </FormHelperText>
      )}

      <Typography variant="body2" sx={{ mt: 1 }} id={productsLabelId}>
        Products
      </Typography>
      <RadioGroup
        row
        aria-labelledby={productsLabelId}
        value={mode}
        onChange={(e) => onMode(e.target.value as ProductMode)}
      >
        <FormControlLabel value="all" control={<Radio />} label="All products" />
        <FormControlLabel value="none" control={<Radio />} label="No products" />
        <FormControlLabel
          value="chosen"
          control={<Radio />}
          label="Chosen categories"
          // Nothing to choose from without the list (a value that already names some keeps it).
          disabled={!canReadCategories && mode !== 'chosen'}
        />
      </RadioGroup>

      {mode === 'chosen' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          <Autocomplete
            multiple
            disabled={disabled}
            options={options}
            value={selected}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_e, picked) => set({ coversCategoryIds: picked.map((p) => p.id) })}
            renderTags={(tags, getTagProps) =>
              tags.map((t, i) => <Chip size="small" label={t.name} {...getTagProps({ index: i })} key={t.id} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Product categories"
                placeholder={categories.length === 0 ? 'No categories yet' : 'Choose categories'}
              />
            )}
            noOptionsText="No product categories"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={value.coversUncategorised}
                onChange={(e) => set({ coversUncategorised: e.target.checked })}
                inputProps={{ 'aria-label': 'Uncategorised products' }}
              />
            }
            label="Uncategorised products"
          />
        </Box>
      )}

      {tuitionProductsLeftOut && (
        // /qa #625 M1: tuition can also be SOLD as a product (MNM's "Tuition Fees" category), and a
        // product line is covered only by the product rule, never by the Tuition tick.
        <FormHelperText error={false} sx={{ ml: 0 }} data-testid="coverage-tuition-product-caption">
          Tuition sold as a product counts as a product. If this discount should cover it, choose that product&apos;s
          category under Products.
        </FormHelperText>
      )}

      {empty ? (
        <FormHelperText sx={{ ml: 0 }} data-testid="coverage-empty-error">
          Choose at least one kind of line the discount covers.
        </FormHelperText>
      ) : (
        <FormHelperText error={false} sx={{ ml: 0 }} data-testid="coverage-summary">
          Covers {describeCoverage(value, names)}
        </FormHelperText>
      )}
      {showReset && (
        <Link
          component="button"
          type="button"
          variant="body2"
          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          onClick={() => {
            setMode(modeOf(defaultCoverage));
            onChange({ ...defaultCoverage, coversCategoryIds: [...defaultCoverage.coversCategoryIds] });
          }}
          disabled={disabled}
        >
          Use the school default ({describeCoverage(defaultCoverage, names)})
        </Link>
      )}
    </FormControl>
  );
}
